import { type VariantProps, cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import React from "react";

import { cn } from "~/components/ui/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center text-sm rounded-lg font-bold transition-colors duration-300 outline-none focus-visible:ring-2 focus-visible:ring-gray-900/30 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "border-2 bg-gray-900 border-gray-900 text-white hover:bg-gray-700 hover:border-gray-700 disabled:bg-gray-300 disabled:border-gray-300",
        outline:
          "border-2 bg-white text-gray-900 border-gray-900 hover:text-gray-700 hover:border-gray-700 disabled:text-gray-300 disabled:border-gray-300",
        destructive:
          "border-2 bg-red-600 border-red-600 text-white hover:bg-red-375 hover:border-red-375 disabled:bg-red-300 disabled:border-red-300",
        link: "text-green-900",
        ghost: "disabled:text-gray-300",
      },
      size: {
        default:
          "h-10 gap-1.5 px-3.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        sm: "h-7 gap-1 rounded-sm px-2.5 text-sm in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "px-9.5 py-3.5 text-md",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button: React.FC<ButtonProps> = ({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}) => {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
};

Button.displayName = "Button";

export { Button };
